import { AdvancedMarker } from '@vis.gl/react-google-maps'
import { isEmpty } from 'lodash'
import React from 'react'

import Circle from '#/components/Maps/components/Circle'
import { FindMyCow } from '#/components/Maps/Markers/types'
import { interpolateColors } from '#/components/Maps/Utils/interpolateColors'

type FindMyCowProps = {
  marker: FindMyCow
  map: google.maps.Map | null
}

/**
 * Componente principal que renderiza círculos e marcadores para cada posição informada.
 * Os círculos recebem cor interpolada conforme a densidade (count).
 */
export default function FindMyCowMarker({ map, marker }: FindMyCowProps) {
  const { c_com_range, positions } = marker

  // Não renderiza nada se não houver posições
  if (isEmpty(positions) || !map) return null

  return (
    <>
      {positions.map(({ count, lat, lng }, index) => {
        // Calcula a cor do círculo baseada na densidade
        const fillColor = interpolatedColor({ count, positions })

        return (
          <React.Fragment key={`find-my-cow-${index}`}>
            <Circle map={map} lat={lat} lng={lng} fillColor={fillColor} radius={c_com_range} />
            <AdvancedMarker position={{ lat, lng }} />
          </React.Fragment>
        )
      })}
    </>
  )
}

/**
 * Retorna os objetos com menor e maior valor de count no array de posições.
 */
const getMinMaxValues = (positions: FindMyCow['positions']) => {
  return positions.reduce(
    (result, obj) => {
      if (obj.count < result.minValue.count) {
        result.minValue = obj
      }
      if (obj.count > result.maxValue.count) {
        result.maxValue = obj
      }
      return result
    },
    { maxValue: positions[0], minValue: positions[0] },
  )
}

interface InterpolatedColorParams {
  count: number
  positions: FindMyCow['positions']
}

/**
 * Calcula a cor interpolada para um dado count, baseado nos valores mínimo e máximo do conjunto.
 */
const interpolatedColor = ({ count, positions }: InterpolatedColorParams) => {
  const { maxValue, minValue } = getMinMaxValues(positions)
  return interpolateColors({
    maxValue: maxValue.count - minValue.count,
    value: count - minValue.count,
  })
}
